import { Button, ButtonMode, DataLoadError, Icon, ICONS, Tooltip } from '@creditsights/styleguide-web';
import classNames from 'classnames/bind';
import React, { useCallback, useState } from 'react';
import { useGlobalModalContext } from '../../../components/GlobalModal';
import { MODAL_TYPES } from '../../../components/GlobalModal/GlobalModal';
import useFetchAllAnalysts from '../../../hooks/analyst/useFetchAllAnalysts';
import { useAddPreferencesModal, useFetchMyContentPreferences } from '../../../hooks/myContentPreferences';
import useFetchFilters from '../../../hooks/tags/useFetchFilters';
import useDeleteMyContentPreferences from '../../../hooks/myContentPreferences/useDeleteMyContentPreferences';
import { useToast } from '../../../hooks/toast/useToast';
import { FilterType } from '../../../models';
import { MAXIMUM_PREFERENCES } from './Preferences/AddPreferencesModal/AddPreferencesModal.constants';
import Preferences from './Preferences/Preferences';
import ManagePreferencesTabHeader from './ManagePreferencesTabHeader/ManagePreferencesTabHeader';
import styles from './ManagePreferencesTab.module.scss';
import { GENERIC_ERROR_TEXT } from '../../../Base.constants';

const cx = classNames.bind(styles);

const REQUIRED_FILTERS = [
  FilterType.region,
  FilterType.sector,
  FilterType.creditGrade,
  FilterType.assetClass,
  FilterType.topic,
  FilterType.report,
];

const ManagePreferencesTab = (): React.ReactElement => {
  const { showModal, closeModal, setError } = useGlobalModalContext();
  const {
    data: preferences,
    isLoading: isLoadingPreferences,
    isError: isErrorPreferences,
    refetch: refetchPreferences,
  } = useFetchMyContentPreferences();
  const { data: filterTypeTags, isLoading: isLoadingTags } = useFetchFilters(REQUIRED_FILTERS);
  const {
    data: allAnalysts,
    isLoading: isLoadingAllAnalysts,
    isError: isErrorAnalysts,
    refetch: refetchAnalysts,
  } = useFetchAllAnalysts();
  const { showToast } = useToast();
  const [preferencesToRemove, setPreferencesToRemove] = useState<number[]>([]);

  const isLoading = isLoadingAllAnalysts || isLoadingPreferences || isLoadingTags;
  const isError = isErrorAnalysts || isErrorPreferences;

  const onAddPreferencesConfirm = useAddPreferencesModal(
    () => {
      showToast('Your Preference has been saved');
      closeModal();
    },
    () => {
      setError(GENERIC_ERROR_TEXT);
    }
  );

  const deletePreferences = useDeleteMyContentPreferences();
  const onDeleteConfirm = useCallback(
    (preferencesToDelete: number[]) => {
      if (!preferences?.length) {
        closeModal();
        return;
      }

      let preferencesToUpdate = [...preferences];
      preferencesToUpdate = preferencesToUpdate.filter(
        (_, preferenceToUpdateIndex) =>
          !preferencesToDelete.some((preferenceToDeleteIndex) => preferenceToDeleteIndex === preferenceToUpdateIndex)
      );

      deletePreferences.mutate(preferencesToUpdate, {
        onSuccess: () => {
          showToast(
            preferencesToDelete.length >= 2 ? 'Your Preferences have been deleted' : 'Your Preference has been deleted'
          );
          closeModal();
          setPreferencesToRemove([]);
        },
        onError: () => setError(GENERIC_ERROR_TEXT),
      });
    },
    [closeModal, preferences, deletePreferences]
  );

  const onAddPreferenceClick = useCallback(() => {
    showModal(MODAL_TYPES.ADD_PREFERENCES, {
      onConfirm: onAddPreferencesConfirm,
    });
  }, [showModal, preferences]);

  const onRemovePreferencesClick = useCallback(() => {
    showModal(MODAL_TYPES.CONFIRM_DELETE_PREFERENCES, {
      onConfirm: () => onDeleteConfirm(preferencesToRemove),
      isMultiplePreferences: preferencesToRemove.length >= 2,
    });
  }, [showModal, preferencesToRemove, preferences]);

  const deleteCount = preferencesToRemove.length > 0 ? `(${preferencesToRemove.length})` : '';

  const renderError = () => {
    const handleRetry = async () => {
      if (isErrorAnalysts) {
        await refetchAnalysts();
      }
      if (isErrorPreferences) {
        await refetchPreferences();
      }
    };

    return <DataLoadError onRetryClick={handleRetry} />;
  };

  const renderContent = () => {
    return (
      <>
        <div className={cx('toolbar')}>
          {!!preferences?.length && (
            <>
              <Tooltip
                disabled={!!preferencesToRemove.length}
                content="Select preference(s) to delete"
                data-testid="remove-preferences-tooltip"
              >
                <div>
                  <Button
                    ariaLabel={`Delete Selected ${deleteCount}`}
                    disabled={isLoading || !preferencesToRemove.length}
                    mode={ButtonMode.TERTIARY}
                    onClick={onRemovePreferencesClick}
                    dataTestId="delete-selected-button"
                  >
                    <Icon name={ICONS.Delete} /> Delete Selected {deleteCount}
                  </Button>
                </div>
              </Tooltip>
              <Tooltip
                content="You have already reached 200 preferences. You can remove existing preferences to create new."
                disabled={isLoading || (preferences?.length ?? 0) < MAXIMUM_PREFERENCES}
                data-testid="limit-tooltip"
              >
                <div>
                  <Button
                    mode={ButtonMode.TERTIARY}
                    onClick={onAddPreferenceClick}
                    disabled={isLoading || (preferences?.length ?? 0) >= 200}
                    dataTestId="add-preference-button"
                  >
                    <Icon name={ICONS.Plus} height={12} width={12} /> Add Interests
                  </Button>
                </div>
              </Tooltip>
            </>
          )}
        </div>
        <Preferences
          allAnalysts={allAnalysts}
          filterTypeTags={filterTypeTags}
          preferences={preferences}
          isLoading={isLoading}
          onAddPreferenceClick={onAddPreferenceClick}
          preferencesToRemove={preferencesToRemove}
          setPreferencesToRemove={setPreferencesToRemove}
        />
      </>
    );
  };

  return (
    <div className={cx('body')} data-testid="manage-preferences-tab">
      <ManagePreferencesTabHeader />
      {isError ? renderError() : renderContent()}
    </div>
  );
};

export default ManagePreferencesTab;
